<template>
  <div>
    <div
      class="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-3"
    >
      <div>
        <h2 class="cg-section-title mb-1">Incoming PPAs</h2>
        <p class="mb-0 text-body-secondary small cg-tabular">{{ summary }}</p>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <div class="input-group input-group-sm cg-search">
          <span class="input-group-text">
            <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
          </span>
          <input
            v-model="search"
            type="search"
            class="form-control"
            placeholder="Search agreements"
            aria-label="Search agreements by name, device, country or company"
            autocomplete="off"
          />
          <button
            v-if="search"
            type="button"
            class="btn btn-outline-secondary"
            aria-label="Clear search"
            @click="search = ''"
          >
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>
        <label class="visually-hidden" :for="sortId">Sort agreements by</label>
        <select
          :id="sortId"
          v-model="sort"
          class="form-select form-select-sm w-auto"
        >
          <option
            v-for="option in SORT_OPTIONS"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <div
          class="btn-group btn-group-sm"
          role="group"
          aria-label="Card layout"
        >
          <button
            type="button"
            class="btn btn-outline-secondary"
            :class="{ active: !compact }"
            :aria-pressed="!compact"
            title="Detailed cards"
            @click="compact = false"
          >
            <font-awesome-icon icon="fa-solid fa-rectangle-list" />
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            :class="{ active: compact }"
            :aria-pressed="compact"
            title="Compact cards"
            @click="compact = true"
          >
            <font-awesome-icon icon="fa-solid fa-list" />
          </button>
        </div>
      </div>
    </div>

    <PpaSupplyCard
      v-for="ppa in visible"
      :key="ppa.key"
      :ppa
      :compact
      @open="$emit('open', $event)"
    />
    <p
      v-if="visible.length === 0"
      class="text-body-secondary small mb-4 p-3 border rounded"
    >
      No agreements match “{{ search }}”.
    </p>
  </div>
</template>

<script setup lang="ts">
import type {
  DerivedDomain,
  DerivedPpa,
} from "@/compliance_groups/fixture/derive";
import type {
  DomainKey,
  PpaKey,
} from "@/compliance_groups/fixture/complianceGroupsTypes";
import { fmt, sum } from "@/compliance_groups/fixture/format";
import { useQueryManager } from "@/composables/useQueryManager";
import { watchDebounced } from "@vueuse/core";
import { computed, ref, useId } from "vue";
import PpaSupplyCard from "./PpaSupplyCard.vue";

type PpaSort = "issues" | "country" | "mwh" | "name";
const SORTS: PpaSort[] = ["issues", "country", "mwh", "name"];
const SORT_OPTIONS: { value: PpaSort; label: string }[] = [
  { value: "issues", label: "Sort by issues" },
  { value: "country", label: "Sort by country" },
  { value: "mwh", label: "Sort by volume" },
  { value: "name", label: "Sort by name" },
];

const props = defineProps<{
  ppas: DerivedPpa[];
  /** The domains the agreements feed; their company names are searchable too. */
  domains: Record<DomainKey, DerivedDomain>;
}>();
defineEmits<{ open: [key: PpaKey] }>();

const sortId = useId();
const { getQueryValue, parsers } = useQueryManager();

// Sort lives in the URL directly; the search box keeps a local value and mirrors it to the URL
// after a pause, so typing does not push a route per keystroke.
const sortQuery = getQueryValue("sort", parsers.enum(SORTS));
const sort = computed<PpaSort>({
  get: () => sortQuery.value ?? "issues",
  set: (value) => (sortQuery.value = value === "issues" ? undefined : value),
});
const layoutQuery = getQueryValue("layout", parsers.enum(["compact"]));
const compact = computed<boolean>({
  get: () => layoutQuery.value === "compact",
  set: (value) => (layoutQuery.value = value ? "compact" : undefined),
});
const searchQuery = getQueryValue("q", parsers.string);
const search = ref(searchQuery.value ?? "");
watchDebounced(
  search,
  (value) => (searchQuery.value = value.trim() || undefined),
  { debounce: 250 }
);

const haystack = (ppa: DerivedPpa): string =>
  [
    ppa.name,
    ppa.seller,
    ppa.country,
    ...ppa.devices.map((d) => d.name),
    ...props.domains[ppa.domain].companies.map((c) => c.name),
  ]
    .join(" ")
    .toLowerCase();

const matching = computed(() => {
  const term = search.value.trim().toLowerCase();
  return term
    ? props.ppas.filter((p) => haystack(p).includes(term))
    : props.ppas;
});

// The subline follows the search, so the totals always describe the agreements on screen.
const summary = computed(() => {
  const shown = matching.value;
  const count =
    shown.length === props.ppas.length
      ? `${shown.length} ${shown.length === 1 ? "agreement" : "agreements"}`
      : `${shown.length} of ${props.ppas.length} agreements`;
  const forecast = sum(shown.map((p) => p.total));
  const delivered = sum(shown.map((p) => p.received));
  return `${count} · ${fmt(forecast)} MWh forecast · ${fmt(delivered)} MWh delivered`;
});

const COMPARE: Record<PpaSort, (a: DerivedPpa, b: DerivedPpa) => number> = {
  // Overdue agreements first, the longest overdue on top; the rest keep their fixture order.
  issues: (a, b) => (b.overdue?.daysLate ?? -1) - (a.overdue?.daysLate ?? -1),
  country: (a, b) =>
    a.country.localeCompare(b.country) || a.name.localeCompare(b.name),
  mwh: (a, b) => b.total - a.total,
  name: (a, b) => a.name.localeCompare(b.name),
};
const visible = computed(() => [...matching.value].sort(COMPARE[sort.value]));
</script>

<style scoped lang="scss">
.cg-search {
  width: 16rem;
}
</style>
